import { useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  AuthProvider,
  CallbackScreen,
  LoginScreen,
  makeQueryClient,
  ProblemError,
  useAuth,
  type OidcAuth,
} from "@csa/web-foundation";
import { I18nProvider, useT } from "./i18n/index";
import { PartyApiProvider } from "./api/party-api";
import { PartyPrincipalProvider, usePartyPrincipal } from "./auth/party-principal";
import { CustomerShell } from "./shell/customer-shell";
import { ROUTES } from "./shell/nav";
import { Placeholder } from "./surfaces/placeholder";
import { Onboarding } from "./onboarding/onboarding";
import { Home } from "./surfaces/home";
import { Consents } from "./consents/consents";
import { Profile } from "./surfaces/profile";
import { Proposal, ProposalDiscovery } from "./proposal/proposal";
import { Claims, Household, Policies, Renewals } from "./surfaces/portfolio";
import { Messages } from "./surfaces/messages";
import { QuoteRequest } from "./surfaces/quote-request";
import { SettingsProvider } from "./settings/appearance";

// The authenticated customer app: the minimal PARTY principal (masked profile) + the
// shell + the routed placeholders. NO staff PrincipalProvider/Me/RBAC, NO governance/
// four-eyes — the party realm carries none of that (ADR 0005).
function GatedApp() {
  const { status } = useAuth();
  const { t } = useT();
  if (status === "loading") return <main className="center">{t("auth.loading")}</main>;
  if (status === "anonymous") return <LoginScreen />;
  return (
    <PartyPrincipalProvider>
      <LinkedGate />
    </PartyPrincipalProvider>
  );
}

// The linking gate (TF1). VERIFIED at source (party-only.guard.ts): an authenticated-
// but-UNLINKED party principal gets a FORBIDDEN 403 from the @PartyOnly
// GET /v1/customer/profile — NOT the 404 the brief assumed (flagged). Treat 403 (or a
// 404 edge) as "not linked → onboard", never a raw error. Linked profile → the shell.
function LinkedGate() {
  const { t } = useT();
  const { profile, isLoading, error } = usePartyPrincipal();
  const notLinked = error instanceof ProblemError && (error.status === 403 || error.status === 404);

  if (isLoading) return <main className="center">{t("auth.loading")}</main>;
  if (notLinked) return <Onboarding />;
  if (profile) {
    return (
      <CustomerShell>
        <Routes>
          {ROUTES.map((r) => (
            <Route
              key={r.key}
              path={r.path.slice(1)}
              element={
                r.key === "home" ? (
                  <Home />
                ) : r.key === "quote" ? (
                  <QuoteRequest />
                ) : r.key === "consents" ? (
                  <Consents />
                ) : r.key === "profile" ? (
                  <Profile />
                ) : r.key === "proposal" ? (
                  <ProposalDiscovery />
                ) : r.key === "policies" ? (
                  <Policies />
                ) : r.key === "household" ? (
                  <Household />
                ) : r.key === "renewals" ? (
                  <Renewals />
                ) : r.key === "claims" ? (
                  <Claims />
                ) : r.key === "messages" ? (
                  <Messages />
                ) : (
                  <Placeholder titleKey={r.labelKey} />
                )
              }
            />
          ))}
          {/* Deep-link into a specific proposal (C0) — the nav lands on /proposal (no id,
              the cold-start), a shared/notified link lands here in-context. */}
          <Route path="proposal/:id" element={<Proposal />} />
          <Route path="*" element={<Navigate to={ROUTES[0]!.path} replace />} />
        </Routes>
      </CustomerShell>
    );
  }
  // Not the not-linked signal — a real error. Calm, with the advisor as the fallback.
  return (
    <main className="center">
      <p role="alert">{t("gate.error")}</p>
    </main>
  );
}

// Provider composition (the console shape, MINUS the staff pieces): no ApprovalProvider
// (no four-eyes), a party-local ApiProvider (ApiClient without the approval gateway).
// The OidcAuth is the AuthHandle the ApiClient consumes AND the SessionAuth the tree
// boots — configured for the csa-customer realm via AppConfig.
export function App({ auth }: { auth: OidcAuth }) {
  const [queryClient] = useState(makeQueryClient);
  return (
    <I18nProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider auth={auth}>
          <PartyApiProvider auth={auth}>
            <SettingsProvider>
              <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <Routes>
                  <Route path="/auth/callback" element={<CallbackScreen auth={auth} />} />
                  <Route path="/*" element={<GatedApp />} />
                </Routes>
              </BrowserRouter>
            </SettingsProvider>
          </PartyApiProvider>
        </AuthProvider>
      </QueryClientProvider>
    </I18nProvider>
  );
}
